import { useId, useState, type ReactElement } from 'react';
import { Button, Modal, PanelHeader } from '../ui/kit/index.js';
import { PixelIcon } from '../ui/PixelIcon.js';
import {
  RELEASE_NOTES,
  RELEASE_NOTE_KIND_LABELS,
  type ReleaseNote,
  type ReleaseNoteKind,
} from './release-notes.js';

const DATE_FORMAT = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/** Format an ISO `YYYY-MM-DD` date as e.g. "Jul 18, 2026", falling back to raw input. */
function formatDate(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? iso : DATE_FORMAT.format(parsed);
}

/** Order in which change groups are shown within a release. */
const KIND_ORDER: readonly ReleaseNoteKind[] = ['feature', 'balance', 'ui', 'fix'];

function ReleaseEntry({ note }: { note: ReleaseNote }): ReactElement {
  // Group highlights by kind so each type shows one shared label, skipping empty kinds.
  const groups = KIND_ORDER
    .map((kind) => ({ kind, texts: note.highlights.filter((h) => h.kind === kind).map((h) => h.text) }))
    .filter((group) => group.texts.length > 0);

  return (
    <section className="changelog-release" aria-label={`Релиз ${note.version}`}>
      <header className="changelog-release-header">
        <span className="changelog-release-version">{note.version}</span>
        <span className="changelog-release-date">{formatDate(note.date)}</span>
      </header>
      {note.title !== undefined ? <p className="changelog-release-title">{note.title}</p> : null}
      <div className="changelog-groups">
        {groups.map((group) => (
          <div key={group.kind} className="changelog-group">
            <span className="changelog-kind" data-kind={group.kind}>
              {RELEASE_NOTE_KIND_LABELS[group.kind]}
            </span>
            <ul className="changelog-group-items">
              {group.texts.map((text, index) => (
                <li key={index} className="changelog-group-item">{text}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ChangelogButton(): ReactElement {
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const close = (): void => setOpen(false);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="compact"
        className="auth-changelog-trigger"
        data-testid="changelog-open"
        title="Обновления"
        onClick={() => setOpen(true)}
      >
        <PixelIcon name="changelog" className="auth-menu-icon" />
        <span className="auth-menu-label">Обновления</span>
      </Button>

      {open ? (
        <Modal
          as="section"
          layer="detail"
          className="changelog-modal"
          labelledBy={titleId}
          testId="changelog-modal"
          onClose={close}
        >
          <PanelHeader
            className="changelog-modal-header"
            title="Обновления"
            headingLevel={2}
            headingId={titleId}
            description="Что изменилось в каждом релизе, новые сверху."
            actions={(
              <Button type="button" variant="ghost" size="sm" aria-label="Закрыть обновления" onClick={close}>×</Button>
            )}
          />

          <div className="changelog-body">
            {RELEASE_NOTES.length === 0 ? (
              <p className="changelog-empty">Пока обновлений нет.</p>
            ) : (
              RELEASE_NOTES.map((note) => <ReleaseEntry key={note.version} note={note} />)
            )}
          </div>

          <Modal.Actions className="changelog-actions">
            <Button type="button" variant="secondary" size="sm" onClick={close}>Закрыть</Button>
          </Modal.Actions>
        </Modal>
      ) : null}
    </>
  );
}
